import { useState, useMemo } from 'react';
import { Skeleton } from '@solid/ui';
import { useParams, useSearchParams, useOutletContext } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Plus,
  Clock,
  CheckSquare,
  LayoutGrid,
  List,
} from 'lucide-react';
import { eventsApi, tasksApi, CalendarEvent, CalendarTask } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';

// Event color mapping (same as in ProjectCalendar)
const eventColors: Record<string, { bg: string; border: string; text: string }> = {
  '#22c55e': { bg: 'bg-success/15', border: 'border-l-success', text: 'text-success' },
  '#f59e0b': { bg: 'bg-warning/15', border: 'border-l-warning', text: 'text-warning' },
  '#e61515': { bg: 'bg-primary/15', border: 'border-l-primary', text: 'text-primary' },
  '#8b5cf6': { bg: 'bg-purple-100', border: 'border-l-purple-500', text: 'text-purple-600' },
  '#06b6d4': { bg: 'bg-cyan-100', border: 'border-l-cyan-500', text: 'text-cyan-600' },
};

const defaultColor = { bg: 'bg-accent/15', border: 'border-l-accent', text: 'text-accent-700' };

function getEventColor(color?: string) {
  if (color && eventColors[color]) {
    return eventColors[color];
  }
  return defaultColor;
}

// Format time for display
function formatEventTime(event: CalendarEvent): string {
  if (event.isAllDay) {
    return 'All day';
  }
  const start = new Date(event.startDate);
  const time = start.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  if (event.endDate) {
    const end = new Date(event.endDate);
    const endTime = end.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    return `${time} – ${endTime}`;
  }
  return time;
}

// Get start of month
function getStartOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);
}

// Get end of month
function getEndOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);
}

// Format month header
function formatMonthYear(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

// Format day header
function formatDayHeader(date: Date): string {
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const isToday = date.toDateString() === today.toDateString();
  const isTomorrow = date.toDateString() === tomorrow.toDateString();

  const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
  const dayNum = date.getDate();
  const month = date.toLocaleDateString('en-US', { month: 'short' });

  if (isToday) {
    return `Today, ${month} ${dayNum}`;
  }
  if (isTomorrow) {
    return `Tomorrow, ${month} ${dayNum}`;
  }
  return `${dayName}, ${month} ${dayNum}`;
}

// Check if date is today
function isToday(date: Date): boolean {
  const today = new Date();
  return date.toDateString() === today.toDateString();
}

// Check if date is a weekend (Saturday = 6, Sunday = 0)
function isWeekend(date: Date): boolean {
  const day = date.getDay();
  return day === 0 || day === 6;
}

// Unified calendar item type
type CalendarItem =
  | { type: 'event'; data: CalendarEvent }
  | { type: 'task'; data: CalendarTask };

// Group events and tasks by day
function groupItemsByDay(
  events: CalendarEvent[],
  tasks: CalendarTask[]
): Map<string, CalendarItem[]> {
  const grouped = new Map<string, CalendarItem[]>();

  // Add events
  events.forEach((event) => {
    const startDate = new Date(event.startDate);
    const endDate = event.endDate ? new Date(event.endDate) : startDate;

    // For multi-day events, add to each day
    const current = new Date(startDate);
    current.setHours(0, 0, 0, 0);

    while (current <= endDate) {
      const key = current.toISOString().split('T')[0];
      const existing = grouped.get(key) || [];
      if (!existing.some((item) => item.type === 'event' && item.data.id === event.id)) {
        existing.push({ type: 'event', data: event });
      }
      grouped.set(key, existing);
      current.setDate(current.getDate() + 1);
    }
  });

  // Add tasks
  tasks.forEach((task) => {
    const dueDate = new Date(task.dueDate);
    const key = dueDate.toISOString().split('T')[0];
    const existing = grouped.get(key) || [];
    existing.push({ type: 'task', data: task });
    grouped.set(key, existing);
  });

  // Sort keys chronologically
  const sortedKeys = Array.from(grouped.keys()).sort();
  const sortedMap = new Map<string, CalendarItem[]>();
  sortedKeys.forEach((key) => {
    const dayItems = grouped.get(key)!;
    // Sort items within each day: events first (by time), then tasks
    dayItems.sort((a, b) => {
      // Events before tasks
      if (a.type === 'event' && b.type === 'task') return -1;
      if (a.type === 'task' && b.type === 'event') return 1;

      // Both events: sort by time
      if (a.type === 'event' && b.type === 'event') {
        if (a.data.isAllDay && !b.data.isAllDay) return -1;
        if (!a.data.isAllDay && b.data.isAllDay) return 1;
        return new Date(a.data.startDate).getTime() - new Date(b.data.startDate).getTime();
      }

      // Both tasks: sort by title
      return a.data.title.localeCompare(b.data.title);
    });
    sortedMap.set(key, dayItems);
  });

  return sortedMap;
}

// Get calendar grid days (includes days from prev/next month to fill the grid)
function getCalendarGridDays(month: Date): Date[] {
  const days: Date[] = [];
  const firstDay = new Date(month.getFullYear(), month.getMonth(), 1);
  const lastDay = new Date(month.getFullYear(), month.getMonth() + 1, 0);

  // Get the day of week for the first day (0 = Sunday)
  const startDayOfWeek = firstDay.getDay();

  // Add days from previous month
  for (let i = startDayOfWeek - 1; i >= 0; i--) {
    const d = new Date(firstDay);
    d.setDate(d.getDate() - i - 1);
    days.push(d);
  }

  // Add days of current month
  for (let d = 1; d <= lastDay.getDate(); d++) {
    days.push(new Date(month.getFullYear(), month.getMonth(), d));
  }

  // Add days from next month to complete the last row only
  const remainder = days.length % 7;
  if (remainder > 0) {
    const remaining = 7 - remainder;
    for (let i = 1; i <= remaining; i++) {
      const d = new Date(lastDay);
      d.setDate(d.getDate() + i);
      days.push(d);
    }
  }

  return days;
}

interface ProjectLayoutContext {
  onAddEvent?: (date?: Date) => void;
  onEventClick?: (event: CalendarEvent) => void;
  onTaskClick?: (task: CalendarTask) => void;
  canEditEvent?: (event: CalendarEvent) => boolean;
  isAgencyMember?: boolean;
  userId?: string;
}

type ViewMode = 'calendar' | 'list';

export function ProjectEvents() {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  const { onAddEvent, onEventClick, onTaskClick, canEditEvent } = useOutletContext<ProjectLayoutContext>() || {};

  // Get initial date from query params if present
  const initialDateStr = searchParams.get('date');
  const initialDate = initialDateStr ? new Date(initialDateStr) : new Date();

  const [currentMonth, setCurrentMonth] = useState(() => getStartOfMonth(initialDate));
  const [viewMode, setViewMode] = useState<ViewMode>(() =>
    window.innerWidth < 1024 ? 'list' : 'calendar'
  );

  // Fetch events for the month
  const monthStart = getStartOfMonth(currentMonth);
  const monthEnd = getEndOfMonth(currentMonth);

  const { data: events = [], isLoading: eventsLoading } = useQuery({
    queryKey: ['events', projectId, monthStart.toISOString()],
    queryFn: () =>
      eventsApi.getByProject(
        projectId!,
        monthStart.toISOString(),
        monthEnd.toISOString()
      ),
    enabled: !!projectId,
    staleTime: 30000,
  });

  // Fetch tasks with due dates for the month
  const { data: tasks = [], isLoading: tasksLoading } = useQuery({
    queryKey: ['calendar-tasks', projectId, monthStart.toISOString()],
    queryFn: () =>
      tasksApi.getByDateRange(
        projectId!,
        monthStart.toISOString(),
        monthEnd.toISOString()
      ),
    enabled: !!projectId,
    staleTime: 30000,
  });

  const isLoading = eventsLoading || tasksLoading;

  // Group events and tasks by day
  const itemsByDay = useMemo(() => groupItemsByDay(events, tasks), [events, tasks]);

  // Get calendar grid days
  const calendarDays = useMemo(() => getCalendarGridDays(currentMonth), [currentMonth]);

  const goToPreviousMonth = () => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() - 1);
    setCurrentMonth(newMonth);
  };

  const goToNextMonth = () => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + 1);
    setCurrentMonth(newMonth);
  };

  const goToCurrentMonth = () => {
    setCurrentMonth(getStartOfMonth(new Date()));
  };

  const isCurrentMonth =
    currentMonth.getMonth() === new Date().getMonth() &&
    currentMonth.getFullYear() === new Date().getFullYear();

  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="w-full">
      {/* Month Navigation */}
      <Card padding="sm" className="mb-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-1 sm:gap-2">
            <span className="text-sm text-grey-500 mr-1 sm:mr-2 hidden sm:inline">
              {events.length + tasks.length} items
            </span>
            <button
              onClick={goToPreviousMonth}
              className="p-1.5 sm:p-2 text-grey-400 hover:text-secondary hover:bg-grey-50 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={goToNextMonth}
              className="p-1.5 sm:p-2 text-grey-400 hover:text-secondary hover:bg-grey-50 rounded-lg transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <h2 className="text-base sm:text-lg font-semibold text-secondary ml-1 sm:ml-2">
              {formatMonthYear(currentMonth)}
            </h2>
            {!isCurrentMonth && (
              <button
                onClick={goToCurrentMonth}
                className="text-sm text-accent hover:underline font-medium"
              >
                Today
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* View Toggle - hidden on mobile (list only) */}
            <div className="hidden sm:flex items-center bg-grey-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('calendar')}
                className={cn(
                  'p-1.5 rounded-md transition-colors',
                  viewMode === 'calendar'
                    ? 'bg-white text-secondary shadow-sm'
                    : 'text-grey-500 hover:text-secondary'
                )}
                title="Calendar view"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={cn(
                  'p-1.5 rounded-md transition-colors',
                  viewMode === 'list'
                    ? 'bg-white text-secondary shadow-sm'
                    : 'text-grey-500 hover:text-secondary'
                )}
                title="List view"
              >
                <List className="w-4 h-4" />
              </button>
            </div>

            {onAddEvent && (
              <Button onClick={() => onAddEvent()} size="sm">
                <Plus className="w-4 h-4" />
                Add Event
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Calendar Grid View */}
      {viewMode === 'calendar' && (
        <Card className="overflow-hidden">
          {/* Day Headers */}
          <div className="grid grid-cols-7 border-b border-grey-200">
            {dayLabels.map((label, idx) => {
              const isWeekendDay = idx === 0 || idx === 6;
              return (
                <div
                  key={label}
                  className={cn(
                    'py-3 text-center text-xs font-semibold uppercase tracking-wide border-r border-grey-100 last:border-r-0',
                    isWeekendDay ? 'text-grey-400 bg-grey-50/50' : 'text-grey-600'
                  )}
                >
                  {label}
                </div>
              );
            })}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7">
            {calendarDays.map((day, idx) => {
              const dayKey = day.toISOString().split('T')[0];
              const dayItems = itemsByDay.get(dayKey) || [];
              const isTodayDate = isToday(day);
              const isCurrentMonthDay = day.getMonth() === currentMonth.getMonth();
              const isWeekendDay = isWeekend(day);
              const isLastRow = idx >= calendarDays.length - 7;
              const isLastCol = (idx + 1) % 7 === 0;

              return (
                <div
                  key={dayKey}
                  className={cn(
                    'min-h-[120px] border-b border-r border-grey-100 p-1.5',
                    isLastRow && 'border-b-0',
                    isLastCol && 'border-r-0',
                    isWeekendDay && 'bg-grey-50/50',
                    !isCurrentMonthDay && 'opacity-40'
                  )}
                >
                  {/* Day Number */}
                  <div className="flex items-center justify-between mb-1">
                    <button
                      onClick={() => onAddEvent?.(day)}
                      className={cn(
                        'w-7 h-7 flex items-center justify-center text-sm font-medium rounded-full transition-colors',
                        isTodayDate
                          ? 'bg-accent text-white'
                          : isWeekendDay
                            ? 'text-grey-400 hover:bg-grey-200'
                            : 'text-secondary hover:bg-grey-100'
                      )}
                    >
                      {day.getDate()}
                    </button>
                  </div>

                  {/* Events/Tasks */}
                  <div className="space-y-0.5 overflow-hidden">
                    {dayItems.slice(0, 3).map((item) => {
                      if (item.type === 'event') {
                        const color = getEventColor(item.data.color);
                        const canEdit = canEditEvent?.(item.data) ?? false;

                        if (canEdit) {
                          return (
                            <button
                              key={`event-${item.data.id}`}
                              onClick={() => onEventClick?.(item.data)}
                              className={cn(
                                'w-full text-left px-1.5 py-0.5 rounded text-[11px] truncate',
                                'border-l-2 transition-colors hover:opacity-80',
                                color.bg,
                                color.border,
                                color.text
                              )}
                              title={item.data.title}
                            >
                              {item.data.title}
                            </button>
                          );
                        }

                        return (
                          <div
                            key={`event-${item.data.id}`}
                            className={cn(
                              'w-full text-left px-1.5 py-0.5 rounded text-[11px] truncate',
                              'border-l-2',
                              color.bg,
                              color.border,
                              color.text
                            )}
                            title={item.data.title}
                          >
                            {item.data.title}
                          </div>
                        );
                      } else {
                        // Task
                        return (
                          <button
                            key={`task-${item.data.id}`}
                            onClick={() => onTaskClick?.(item.data)}
                            className={cn(
                              'w-full text-left px-1.5 py-0.5 rounded text-[11px] truncate',
                              'border-l-2 transition-colors hover:opacity-80 flex items-center gap-1',
                              'bg-success/15 border-l-success text-success'
                            )}
                            title={item.data.title}
                          >
                            <CheckSquare className="w-2.5 h-2.5 shrink-0" />
                            <span className="truncate">{item.data.title}</span>
                          </button>
                        );
                      }
                    })}
                    {dayItems.length > 3 && (
                      <button
                        onClick={() => {
                          setViewMode('list');
                        }}
                        className="text-[10px] text-grey-500 hover:text-accent pl-1.5 font-medium"
                      >
                        +{dayItems.length - 3} more
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div className="max-w-3xl mx-auto">
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} aria-busy>
                  <Skeleton className="mb-2 h-5 w-32" />
                  <Skeleton className="h-20 rounded-xl" />
                </div>
              ))}
            </div>
          ) : itemsByDay.size === 0 ? (
            <EmptyState
              icon={Calendar}
              title="No events or tasks this month"
              description="There are no scheduled events or tasks with due dates for this month."
              action={
                onAddEvent && (
                  <Button variant="outline" onClick={() => onAddEvent()}>
                    <Plus className="w-4 h-4" />
                    Add an event
                  </Button>
                )
              }
            />
          ) : (
            <div className="space-y-6">
              {Array.from(itemsByDay.entries()).map(([dateKey, dayItems]) => {
                const date = new Date(dateKey + 'T00:00:00');
                const isTodayDate = isToday(date);

                return (
                  <div key={dateKey}>
                    {/* Day Header */}
                    <div className="flex items-center gap-2 mb-3">
                      <h3
                        className={cn(
                          'text-sm font-semibold',
                          isTodayDate ? 'text-accent' : 'text-secondary'
                        )}
                      >
                        {formatDayHeader(date)}
                      </h3>
                      {isTodayDate && (
                        <span className="px-2 py-0.5 text-[10px] font-medium bg-accent text-white rounded-full">
                          TODAY
                        </span>
                      )}
                    </div>

                    {/* Day Items */}
                    <Card className="divide-y divide-grey-100">
                      {dayItems.map((item) => {
                        if (item.type === 'event') {
                          const event = item.data;
                          const color = getEventColor(event.color);
                          const canEdit = canEditEvent?.(event) ?? false;

                          // Shared content for both clickable and non-clickable versions
                          const eventContent = (
                            <>
                              {/* Color indicator */}
                              <div
                                className={cn(
                                  'w-1 self-stretch rounded-full shrink-0',
                                  color.border.replace('border-l-', 'bg-')
                                )}
                              />

                              {/* Event content */}
                              <div className="flex-1 min-w-0">
                                <h4
                                  className={cn(
                                    'font-medium text-secondary truncate',
                                    color.text
                                  )}
                                >
                                  {event.title}
                                </h4>
                                <div className="flex items-center gap-2 mt-1 text-sm text-grey-500">
                                  <Clock className="w-3.5 h-3.5" />
                                  <span>{formatEventTime(event)}</span>
                                </div>
                                {event.description && (
                                  <p className="text-sm text-grey-500 mt-2 line-clamp-2">
                                    {event.description}
                                  </p>
                                )}
                              </div>
                            </>
                          );

                          // Render clickable button for users with edit permission
                          if (canEdit) {
                            return (
                              <button
                                key={`event-${event.id}`}
                                onClick={() => onEventClick?.(event)}
                                className={cn(
                                  'w-full text-left p-4 hover:bg-grey-50 transition-colors',
                                  'flex items-start gap-4'
                                )}
                              >
                                {eventContent}
                              </button>
                            );
                          }

                          // Render non-clickable div for users without permission
                          return (
                            <div
                              key={`event-${event.id}`}
                              className={cn(
                                'w-full text-left p-4',
                                'flex items-start gap-4'
                              )}
                            >
                              {eventContent}
                            </div>
                          );
                        } else {
                          // Task
                          const task = item.data;

                          return (
                            <button
                              key={`task-${task.id}`}
                              onClick={() => onTaskClick?.(task)}
                              className={cn(
                                'w-full text-left p-4 hover:bg-grey-50 transition-colors',
                                'flex items-start gap-4'
                              )}
                            >
                              {/* Color indicator */}
                              <div
                                className={cn(
                                  'w-1 self-stretch rounded-full shrink-0',
                                  'bg-success'
                                )}
                              />

                              {/* Task content */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <CheckSquare className="w-4 h-4 text-success shrink-0" />
                                  <h4 className="font-medium text-secondary truncate">
                                    <span className="text-grey-400 font-normal">Due:</span> {task.title}
                                  </h4>
                                </div>
                                <div className="flex items-center gap-2 mt-1 text-sm text-grey-500">
                                  <span className="text-xs bg-grey-100 px-2 py-0.5 rounded">
                                    {task.list.name}
                                  </span>
                                  {task.assignee && (
                                    <span className="text-xs text-grey-400">
                                      {task.assignee.name}
                                    </span>
                                  )}
                                </div>
                                {task.description && (
                                  <p className="text-sm text-grey-500 mt-2 line-clamp-2">
                                    {task.description}
                                  </p>
                                )}
                              </div>
                            </button>
                          );
                        }
                      })}
                    </Card>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
